import { defineStore } from "pinia";
import { type AccountInfo, type AuthenticationResult, EventType, InteractionRequiredAuthError } from "@azure/msal-browser";
import { msalInstance, scopeConfig } from "@/config/authConfig";

let msalEventCallbackId: string | null = null;

export const useAuthStore = defineStore("auth", {
    state: () => ({
        ready: false as boolean,
        _initPromise: null as null | Promise<void>,
        error: null as null | string,
        loggingOut: false as boolean,

        account: null as null | AccountInfo,
        isAuthenticated: false as boolean,
    }),
    getters: {
        name: (s) => s.account?.idTokenClaims?.name as string || "",
        givenName: (s) => s.account?.idTokenClaims?.givenName as string || "",
        surname: (s) => s.account?.idTokenClaims?.surname as string || "",
        email: (s) => s.account?.idTokenClaims?.email as string || "",
    },
    actions: {
        async init() {
            if (this._initPromise) {
                return this._initPromise;
            }

            this._initPromise = (async () => {
                await msalInstance.initialize();

                if (!msalEventCallbackId) {
                    msalEventCallbackId = msalInstance.addEventCallback((e) => {
                        if (e.eventType === EventType.LOGIN_SUCCESS) {
                            const res = e.payload as AuthenticationResult;
                            if (res?.account) {
                                this._applyAccount(res.account);
                            }
                        }
                        if (e.eventType === EventType.LOGIN_FAILURE && e.error) {
                            this.error = e.error?.message ?? "Unknown error";
                            msalInstance.loginRedirect({
                                scopes: scopeConfig.scopes,
                                redirectStartPage: globalThis.location.href,
                            });
                        }
                    });
                }

                // Handle redirect promise if returning from a redirect login
                const redirectResult = await msalInstance.handleRedirectPromise();
                if (redirectResult?.account) {
                    // If a valid account is found, apply it
                    this._applyAccount(redirectResult.account)
                } else {
                    // No redirect, try to get active account
                    const activeAccount = msalInstance.getActiveAccount();
                    if (activeAccount) {
                        this._applyAccount(activeAccount);
                    } else {
                        // No active account, fallback to first account if any
                        const accounts = msalInstance.getAllAccounts()
                        if (accounts.length) this._applyAccount(accounts[0])
                    }
                }

                this.ready = true;
            })();

            return this._initPromise;
        },

        async login() {
            await msalInstance.loginRedirect({
                scopes: scopeConfig.scopes,
                redirectStartPage: globalThis.location.href,
            });
        },

        async logout() {
            this.loggingOut = true;
            this._clearAccount();
            await msalInstance.logoutRedirect();
        },

        _clearAccount() {
            this.account = null;
            this.isAuthenticated = false;
        },

        _applyAccount(account: AccountInfo) {
            msalInstance.setActiveAccount(account);
            this.account = account;
            this.isAuthenticated = true;
        },

        async getAuthToken(extraScopes: string[] = []) {
            const scopes = [...scopeConfig.scopes, ...extraScopes];
            await msalInstance.initialize();
            const account = this.account ?? msalInstance.getActiveAccount();
            if (!account) return null;

            try {
                const result = await msalInstance.acquireTokenSilent({
                    account,
                    scopes,
                });

                return result.accessToken;
            } catch (err) {
                if (err instanceof InteractionRequiredAuthError) {
                    await msalInstance.acquireTokenRedirect({ scopes, redirectStartPage: globalThis.location.href });
                    return null;
                }
                throw err
            }
        },
    }
});
